import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  FaUserTie,
  FaHandsHelping,
  FaChartLine,
  FaSearch,
  FaHome,
  FaRocket,
  FaShieldAlt,
  FaGlobeAsia,
} from "react-icons/fa";
import "../styles/user/About.css";

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 🌿 Một kiểu animation đơn giản
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const introSections = [
    {
      title: "Kết nối CTV và Chủ Nhà",
      desc: "SHome giúp Cộng Tác Viên dễ dàng xem, tìm và giới thiệu phòng trọ cho khách hàng một cách minh bạch, nhanh chóng và hiệu quả.",
      img: "https://i.pinimg.com/1200x/4f/1b/37/4f1b37fee9584db04053e43f9e1dac8e.jpg",
    },
    {
      title: "Công cụ hỗ trợ CTV tối đa",
      desc: "Giao diện trực quan, cập nhật nhanh thông tin phòng mới nhất, giúp CTV tiếp cận khách hàng hiệu quả và tiện lợi hơn bao giờ hết.",
      img: "https://i.pinimg.com/1200x/1f/12/3d/1f123d913f5df0fec0c71fd6917c9b42.jpg",
    },
    {
      title: "Hệ thống minh bạch cho chủ nhà",
      desc: "Chủ nhà có thể đăng phòng dễ dàng, thông tin được xác minh rõ ràng và hỗ trợ quảng bá trực tiếp qua đội ngũ CTV.",
      img: "https://i.pinimg.com/1200x/b2/eb/2a/b2eb2a3e33a19faa7f05e8fad3dad57c.jpg",
    },
    {
      title: "Hệ thống quản lý thông minh",
      desc: "Theo dõi trạng thái phòng, tình hình khách hàng và hiệu quả giới thiệu ngay trong một nền tảng duy nhất.",
      img: "https://i.pinimg.com/1200x/7d/9b/36/7d9b36e7d849c6759ab285524bdccd19.jpg",
    },
    {
      title: "Cộng đồng năng động",
      desc: "SHome đang xây dựng cộng đồng hàng trăm CTV năng động, hỗ trợ lẫn nhau và chia sẻ kinh nghiệm hiệu quả.",
      img: "https://i.pinimg.com/1200x/d5/95/a4/d595a42f36d857054af0843d87e7f61b.jpg",
    },
    {
      title: "Định hướng tương lai",
      desc: "SHome hướng đến trở thành nền tảng bất động sản kết nối toàn diện, nơi CTV và chủ nhà cùng phát triển bền vững.",
      img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1000&q=80",
    },
  ];

  const features = [
    { icon: <FaSearch />, title: "Tìm phòng dễ dàng", desc: "Bộ lọc thông minh giúp CTV tìm đúng phòng cần giới thiệu." },
    { icon: <FaHandsHelping />, title: "Kết nối nhanh", desc: "Chủ nhà và CTV làm việc trực tiếp, minh bạch và tiện lợi." },
    { icon: <FaHome />, title: "Nguồn tin thật", desc: "Phòng trọ được xác minh thông tin rõ ràng, đảm bảo uy tín." },
    { icon: <FaChartLine />, title: "Hoa hồng hấp dẫn", desc: "CTV nhận thưởng minh bạch theo mỗi phòng giới thiệu thành công." },
    { icon: <FaUserTie />, title: "Quản lý chuyên nghiệp", desc: "Theo dõi khách hàng, doanh thu, hoa hồng một cách dễ dàng." },
    { icon: <FaRocket />, title: "Cơ hội phát triển", desc: "Xây dựng mạng lưới CTV bền vững cùng SHome trên toàn quốc." },
    { icon: <FaShieldAlt />, title: "Bảo mật & uy tín", desc: "Hệ thống bảo mật dữ liệu và xác minh chặt chẽ, đảm bảo an toàn." },
    { icon: <FaGlobeAsia />, title: "Mở rộng toàn quốc", desc: "Kết nối hàng nghìn CTV và chủ nhà trên khắp Việt Nam." },
  ];

  return (
    <div className="about-page">
      <Header />

      {/* HERO */}
      <motion.section
        className="hero-section-gray"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <h1 className="hero-title">
          Nền tảng dành cho <span className="highlight">Cộng Tác Viên</span>
        </h1>
        <p className="hero-desc">
          SHome giúp CTV kết nối chủ nhà, xem phòng và giới thiệu khách hàng
          nhanh chóng, hiệu quả và minh bạch.
        </p>
      </motion.section>

      {/* INTRO */}
      {introSections.map((s, i) => (
        <motion.section
          key={i}
          className={`intro-section ${i % 2 !== 0 ? "reverse" : ""}`}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="intro-text glass" variants={fadeUp}>
            <h2 className="section-title">{s.title}</h2>
            <p className="intro-desc">{s.desc}</p>
          </motion.div>

          <motion.img
            src={s.img}
            alt={s.title}
            className="intro-img"
            variants={fadeUp}
          />
        </motion.section>
      ))}

      {/* FEATURES */}
      <section className="features-section">
        <motion.h2
          className="section-title"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Vì sao <span className="highlight">CTV chọn SHome?</span>
        </motion.h2>

        <div className="features-grid">
          {features.map((f, i) => (
            <motion.div
              key={i}
              className="feature-card glass"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              transition={{ delay: i * 0.05 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="feature-icon">{f.icon}</div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
